import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getLinkFromEntityNode } from '@atlassian/twg-entity-node-utils/entity-nodes';

import { getConfig } from '../../../../../config';

const isUrlString = (v: unknown): v is string => typeof v === 'string' && /^https?:\/\//i.test(v);

/**
 * Checks if a value is a GraphStore object with a single value field
 * Used for IntObject, StringObject, TimestampObject, etc.
 */
const isSingleValueObject = (value: any): value is { [key: string]: any } => {
	return value && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Extracts the scalar value from a single-value GraphStore object
 * Handles: IntObject, StringObject, TimestampObject, etc.
 */
export const extractSingleValue = (value: any): any => {
	if (!isSingleValueObject(value)) {
		return undefined;
	}
	// Try common field names for single values
	if ('value' in value) {
		return value.value;
	}
	if ('timestamp' in value) {
		return value.timestamp;
	}
	if ('int_val' in value) {
		return value.int_val;
	}
	if ('str_val' in value) {
		return value.str_val;
	}
	return undefined;
};

/**
 * Extracts the values array from a list GraphStore object
 * Handles: IntListObject, StringListObject, TimestampListObject, etc.
 */
export const extractListValues = (value: any): any[] => {
	if (!isSingleValueObject(value)) {
		return [];
	}
	// Try common field names for list values
	if (Array.isArray(value.values)) {
		return value.values;
	}
	if (Array.isArray(value.int_vals)) {
		return value.int_vals;
	}
	if (Array.isArray(value.str_vals)) {
		return value.str_vals;
	}
	if (Array.isArray(value.timestamps)) {
		return value.timestamps;
	}
	return [];
};

/**
 * Checks if a value is a GraphStore timestamp object
 * e.g., { __typename: "GraphStoreCypherQueryV2TimestampObject", timestamp: 1767116300873 }
 */
export const isTimestampObject = (
	value: any,
): value is { __typename: string; timestamp: number } => {
	return isSingleValueObject(value) && typeof value.timestamp === 'number';
};

/**
 * Checks if a value is a GraphStore timestamp list object
 * e.g., { __typename: "GraphStoreCypherQueryV2TimestampListObject", timestamps: [...] }
 */
export const isTimestampListObject = (
	value: any,
): value is { __typename: string; timestamps: number[] } => {
	return isSingleValueObject(value) && Array.isArray(value.timestamps);
};

/**
 * Checks if a value is a GraphStore int object
 * e.g., { __typename: "GraphStoreCypherQueryV2IntObject", int_val: 42 }
 */
export const isIntObject = (value: any): value is { __typename: string; int_val: number } => {
	return isSingleValueObject(value) && typeof value.int_val === 'number';
};

/**
 * Checks if a value is a GraphStore int list object
 * e.g., { __typename: "GraphStoreCypherQueryV2IntListObject", int_vals: [...] }
 */
export const isIntListObject = (
	value: any,
): value is { __typename: string; int_vals: number[] } => {
	return isSingleValueObject(value) && Array.isArray(value.int_vals);
};

/**
 * Checks if a value is a GraphStore string object
 * e.g., { __typename: "GraphStoreCypherQueryV2StringObject", str_val: "hello" }
 */
export const isStringObject = (value: any): value is { __typename: string; str_val: string } => {
	return isSingleValueObject(value) && typeof value.str_val === 'string';
};

/**
 * Checks if a value is a GraphStore string list object
 * e.g., { __typename: "GraphStoreCypherQueryV2StringListObject", str_vals: [...] }
 */
export const isStringListObject = (
	value: any,
): value is { __typename: string; str_vals: string[] } => {
	return isSingleValueObject(value) && Array.isArray(value.str_vals);
};

/**
 * Formats a timestamp (milliseconds since epoch) as a localized date/time string
 */
export const formatTimestamp = (timestamp: number): string => {
	const date = new Date(timestamp);

	// en-CA gives YYYY-MM-DD
	const datePart = date.toLocaleDateString('en-CA');

	// en-US gives 12:00 PM
	const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

	return `${datePart} ${timePart}`;
};

/**
 * Formats a list of timestamps as comma-separated date/time strings
 */
export const formatTimestampList = (timestamps: number[]): string => {
	return timestamps.map(formatTimestamp).join(', ');
};

/**
 * Formats a list of values as a comma-separated string
 */
export const formatList = (values: any[]): string => {
	return values.map((v) => String(v)).join(', ');
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	value !== null && typeof value === 'object' && !Array.isArray(value);

const normalizeHydratedEntityData = (value: unknown, data: unknown): unknown => {
	if (!isRecord(data) || typeof data.id === 'string') {
		return data;
	}

	const typename = typeof data.__typename === 'string' ? data.__typename : undefined;
	const prefixedId = typename ? data[`${typename}_id`] : undefined;

	if (typeof prefixedId === 'string') {
		return { ...data, id: prefixedId };
	}

	if (isRecord(value) && typeof value.id === 'string') {
		return { ...data, id: value.id };
	}

	return data;
};

export type SmartLinkUrlOptions = {
	cloudId?: string;
};

export const getSmartLinkUrl = (value: any, options?: SmartLinkUrlOptions): string | undefined => {
	if (value === null || value === undefined) {
		return undefined;
	}
	const { atlassianHomeUrl, defaultSiteCloudId, orgId } = getConfig();
	const cloudId = options?.cloudId ?? defaultSiteCloudId;
	const data =
		value && typeof value === 'object' && !Array.isArray(value) ? (value as any).data : undefined;

	const firstAttempt = getLinkFromEntityNode(value, `${atlassianHomeUrl}/o/${orgId}`, cloudId);
	if (firstAttempt !== null && firstAttempt.length !== 0) {
		return firstAttempt;
	}

	if (
		data &&
		typeof data === 'object' &&
		!Array.isArray(data) &&
		fg('dtac_rovo_agent_smart_links')
	) {
		const normalizedData = normalizeHydratedEntityData(value, data);
		const dataAttempt = getLinkFromEntityNode(
			normalizedData,
			`${atlassianHomeUrl}/o/${orgId}`,
			cloudId,
		);
		if (dataAttempt !== null && dataAttempt.length !== 0) {
			return dataAttempt;
		}
	}

	if (isUrlString(value)) {
		return value;
	}

	if (isUrlString(value?.url)) {
		return value.url;
	}

	if (isUrlString(value?.webUrl)) {
		return value.webUrl;
	}

	if (isUrlString(value?.href)) {
		return value.href;
	}

	// Check for links at the top level (e.g., normalized entities where
	// ConfluencePage_links has been stripped to just "links")
	if (value.links && typeof value.links === 'object' && !Array.isArray(value.links)) {
		const base = value.links.base;
		const webUi = value.links.webUi;

		if (typeof base === 'string' && typeof webUi === 'string') {
			const baseNormalized = base.replace(/\/$/, '');
			const webUiNormalized = webUi.replace(/^\//, '');
			const combined = `${baseNormalized}/${webUiNormalized}`;

			if (isUrlString(combined)) {
				return combined;
			}
		}
	}

	// Check for top-level fields ending in `_url` or `_webUrl`
	for (const topKey of Object.keys(value)) {
		if ((topKey.endsWith('_webUrl') || topKey.endsWith('_url')) && isUrlString(value[topKey])) {
			return value[topKey];
		}
	}

	// Check for top-level fields ending in `_links` with { base, webUi }
	for (const topKey of Object.keys(value)) {
		if (!topKey.endsWith('_links')) {
			continue;
		}
		const links = value[topKey];
		if (!links || typeof links !== 'object' || Array.isArray(links)) {
			continue;
		}
		const base = links.base;
		const webUi = links.webUi;
		if (typeof base === 'string' && typeof webUi === 'string') {
			const baseNormalized = base.replace(/\/$/, '');
			const webUiNormalized = webUi.replace(/^\//, '');
			const combined = `${baseNormalized}/${webUiNormalized}`;
			if (isUrlString(combined)) {
				return combined;
			}
		}
	}

	if (data && typeof data === 'object' && !Array.isArray(data)) {
		if (isUrlString((data as any).url)) {
			return (data as any).url;
		}

		if (isUrlString((data as any).webUrl)) {
			return (data as any).webUrl;
		}

		for (const dataKey of Object.keys(data)) {
			if (
				(dataKey.endsWith('_webUrl') || dataKey.endsWith('_url')) &&
				isUrlString((data as any)[dataKey])
			) {
				return (data as any)[dataKey];
			}
		}

		if (
			(data as any).links &&
			typeof (data as any).links === 'object' &&
			!Array.isArray((data as any).links)
		) {
			const base = (data as any).links.base;
			const webUi = (data as any).links.webUi;

			if (typeof base === 'string' && typeof webUi === 'string') {
				const baseNormalized = base.replace(/\/$/, '');
				const webUiNormalized = webUi.replace(/^\//, '');
				const combined = `${baseNormalized}/${webUiNormalized}`;

				if (isUrlString(combined)) {
					return combined;
				}
			}
		}

		// Fallback: look for any field ending in `_links` that contains a { base, webUi } pair
		for (const dataKey of Object.keys(data)) {
			if (!dataKey.endsWith('_links')) {
				continue;
			}

			const links = (data as any)[dataKey];
			if (!links || typeof links !== 'object' || Array.isArray(links)) {
				continue;
			}

			const base = links.base;
			const webUi = links.webUi;

			if (typeof base === 'string' && typeof webUi === 'string') {
				const baseNormalized = base.replace(/\/$/, '');
				const webUiNormalized = webUi.replace(/^\//, '');
				const combined = `${baseNormalized}/${webUiNormalized}`;

				if (isUrlString(combined)) {
					return combined;
				}
			}
		}
	}

	return undefined;
};

/**
 * Extracts a display name from a value object, checking for common name fields.
 * If the key contains an underscore, extracts the part after it and also checks for that field.
 * Checks in order: displayName, name, title, summary, and the extracted field name (if applicable).
 */
export const getDisplayName = (key: string, value: any): string | undefined => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return undefined;
	}

	const underscoreIndex = key.indexOf('_');
	const extractedFieldName = underscoreIndex !== -1 ? key.substring(underscoreIndex + 1) : null;

	const priorityFields = ['displayName', 'name', 'title', 'summary'] as const;

	// Check direct fields first
	for (const field of priorityFields) {
		if ((value as any)[field] != null && typeof (value as any)[field] !== 'object') {
			return String((value as any)[field]);
		}
	}

	// Check nested data structure (e.g., entity.data.field)
	const data = (value as any).data;
	if (data && typeof data === 'object' && !Array.isArray(data)) {
		for (const field of priorityFields) {
			if ((data as any)[field] != null && typeof (data as any)[field] !== 'object') {
				return String((data as any)[field]);
			}
		}

		for (const field of priorityFields) {
			for (const dataKey of Object.keys(data)) {
				if (
					dataKey.endsWith(`_${field}`) &&
					(data as any)[dataKey] != null &&
					typeof (data as any)[dataKey] !== 'object'
				) {
					return String((data as any)[dataKey]);
				}
			}
		}

		if (
			extractedFieldName &&
			(data as any)[extractedFieldName] != null &&
			typeof (data as any)[extractedFieldName] !== 'object'
		) {
			return String((data as any)[extractedFieldName]);
		}
	}

	if (
		extractedFieldName &&
		(value as any)[extractedFieldName] != null &&
		typeof (value as any)[extractedFieldName] !== 'object'
	) {
		return String((value as any)[extractedFieldName]);
	}

	return undefined;
};
